import { useMutation } from "@tanstack/react-query"

interface SendOTPRequest {
  mobile: string
}

interface SendOTPResponse {
  message?: string
  status?: string
}

interface VerifyOTPRequest {
  mobile: string
  otp: string
}

interface VerifyOTPResponse {
  message?: string
  status?: string
  user?: {
    id: string
    mobile: string
    firstName: string
    lastName: string
    email?: string
  }
  token?: string
}

interface SignupRequest {
  mobile: string
  firstname: string
  lastname: string
  password: string
}

interface SignupResponse {
  message?: string
  status?: string
  user?: {
    id: string
    mobile: string
    firstName: string
    lastName: string
    email?: string
  }
  token?: string
}

function createHttpError(message: string, status: number) {
  const error = new Error(message) as Error & { status: number }
  error.status = status
  return error
}

async function throwApiError(res: Response, fallbackMessage: string): Promise<never> {
  const status = res.status
  const contentType = (res.headers.get("content-type") || "").toLowerCase()
  let message = ""

  if (contentType.includes("application/json")) {
    const errorJson = await res.json().catch(() => ({} as any))
    if (typeof errorJson?.message === "string") {
      message = errorJson.message
    } else if (typeof errorJson?.error === "string") {
      message = errorJson.error
    }
  } else {
    const errorText = await res.text().catch(() => "")
    const looksLikeHtml = /<!doctype html|<html|<body|<head|<title/i.test(errorText)
    const sanitizedText = errorText.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()

    if (!looksLikeHtml && sanitizedText.length > 0 && sanitizedText.length < 220) {
      message = sanitizedText
    }
  }

  if (!message && status >= 500) {
    message = "Service temporarily unavailable. Please try again."
  }

  if (!message) {
    message = fallbackMessage
  }

  throw createHttpError(message, status)
}

async function sendOTP(data: SendOTPRequest): Promise<SendOTPResponse> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/auth/sendOTP`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  
  if (!res.ok) {
    await throwApiError(res, "Failed to send OTP")
  }

  return res.json()
}

async function verifyOTP(data: VerifyOTPRequest): Promise<VerifyOTPResponse> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/auth/verifyOTP`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (res.status === 401) {
    throw createHttpError("User not registered", 401)
  }
  
  if (!res.ok) {
    await throwApiError(res, "Failed to verify OTP")
  }
  
  // Get response as text first, then try to parse as JSON
  const textResult = await res.text()
  try {
    // First parse: if response is a JSON string, this gives us the string content
    const firstParse = JSON.parse(textResult)
    
    // If first parse gives us a string, try parsing again (double-encoded JSON)
    if (typeof firstParse === 'string') {
      try {
        return JSON.parse(firstParse)
      } catch {
        // If second parse fails, return the string as message
        return {
          message: firstParse,
          status: "success",
        }
      }
    }
    
    // If first parse gives us an object, return it directly
    return firstParse
  } catch {
    // If it's not valid JSON, treat it as a plain string message
    return {
      message: textResult,
      status: "success",
    }
  }
}

async function verifyOtherOTP(data: VerifyOTPRequest): Promise<VerifyOTPResponse> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/auth/verfiyOtherOTP`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (res.status === 401) {
    throw createHttpError("User not registered", 401)
  }
  
  if (!res.ok) {
    await throwApiError(res, "Failed to verify OTP")
  }
  
  // Get response as text first, then try to parse as JSON
  const textResult = await res.text()
  try {
    // First parse: if response is a JSON string, this gives us the string content
    const firstParse = JSON.parse(textResult)
    
    // If first parse gives us a string, try parsing again (double-encoded JSON)

  

    if (typeof firstParse.mobile === 'string') {
      try {
        return JSON.parse(firstParse)
      } catch {
        // If second parse fails, return the string as message
        return {
          message: firstParse,
          status: "success",
        }
      }
    }


    
    // If first parse gives us an object, return it directly
    return {
          message: firstParse.mobile,
          status: "success",
        }
  } catch {
    // If it's not valid JSON, treat it as a plain string message
    return {
      message: textResult,
      status: "success",
    }
  }
}

async function signup(data: SignupRequest): Promise<SignupResponse> {
  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
  const res = await fetch(`${apiUrl}/v1/auth/signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  
  // if (!res.ok) {
  //   const error = await res.json().catch(() => ({}))
  //   throw new Error(error.message || "Failed to sign up")
  // }
  
  // return res.json()


  if (!res.ok) {
    await throwApiError(res, "Failed to register")
  }


  const textResult = await res.text()
  try {
    // First parse: if response is a JSON string, this gives us the string content
    const firstParse = JSON.parse(textResult)
    
    // If first parse gives us a string, try parsing again (double-encoded JSON)
    if (typeof firstParse === 'string') {
      try {
        return JSON.parse(firstParse)
      } catch {
        // If second parse fails, return the string as message
        return {
          message: firstParse,
          status: "success",
        }
      }
    }
    
    // If first parse gives us an object, return it directly
    return firstParse
  } catch {
    // If it's not valid JSON, treat it as a plain string message
    return {
      message: textResult,
      status: "success",
    }
  }
}

export function useSendOTP() {
  return useMutation({
    mutationFn: sendOTP,
  })
}



export function useVerifyOTP() {
  return useMutation({
    mutationFn: verifyOTP,
  })
}

export function useVerifyOtherOTP() {
  return useMutation({
    mutationFn: verifyOtherOTP,
  })
}


export function useSignup() {
  return useMutation({
    mutationFn: signup,
  })
}

